/**
 * SKY UTILS — shared colour / easing / time helpers and the day-night sky
 * palette used across the sky sub-renderers (gradient, sea, clouds, stars,
 * celestial). Pure: no canvas drawing, no game state.
 */

import { computeMockupUiScale } from "../../layout/mockup-ui-scale.js";
import { resolveGridWidthPx } from "../../layout/responsive-board.js";

export function clamp01(v) {
  if (v <= 0) return 0;
  if (v >= 1) return 1;
  return v;
}

export function lerp(a, b, u) {
  return a + (b - a) * u;
}

export function lerpRgb(c0, c1, u) {
  return [
    Math.round(lerp(c0[0], c1[0], u)),
    Math.round(lerp(c0[1], c1[1], u)),
    Math.round(lerp(c0[2], c1[2], u)),
  ];
}

export function rgb(c, alpha) {
  if (alpha == null) return "rgb(" + c[0] + ", " + c[1] + ", " + c[2] + ")";
  return "rgba(" + c[0] + ", " + c[1] + ", " + c[2] + ", " + alpha + ")";
}

export function normalizeTimeOfDay(timeOfDay) {
  var t = Number(timeOfDay);
  if (!isFinite(t)) return 0;
  t = t % 1;
  if (t < 0) t += 1;
  return t;
}

export function timeSec(currentTime) {
  // 0 is a valid hour-clock time (the very start), so accept >= 0; only fall back
  // to wall-clock when no finite time was supplied.
  var ms = currentTime != null && isFinite(currentTime) && currentTime >= 0
    ? currentTime
    : (typeof performance !== "undefined" ? performance.now() : Date.now());
  return ms * 0.001;
}

/**
 * 0..1 "night-ness" for decor that swaps between the day and night mockup looks
 * (clouds, sea-line highlight): 0 through day/dawn, ramps up across dusk, 1 in
 * deep night, ramps back down before dawn.
 */
export function nightAmount(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  if (t < 0.42) return 0;
  if (t < 0.62) return (t - 0.42) / 0.2;
  if (t <= 0.9) return 1;
  return Math.max(0, 1 - (t - 0.9) / 0.1);
}

/** Interpolate a colour field across a keyframe table at normalized time t. */
export function sampleKeyframes(keyframes, t, field) {
  t = normalizeTimeOfDay(t);
  for (var i = 0; i < keyframes.length - 1; i++) {
    var a = keyframes[i];
    var b = keyframes[i + 1];
    if (t >= a.t && t <= b.t) {
      var span = b.t - a.t;
      var u = span > 0 ? (t - a.t) / span : 0;
      return lerpRgb(a[field], b[field], u);
    }
  }
  return keyframes[keyframes.length - 1][field].slice();
}

/** Resolve the mockup UI scale from grid width (stable when side columns expand). */
export function mockupUiScaleFromOpts(opts, boardBounds) {
  var layout = opts && opts.layout;
  var uiW = layout ? resolveGridWidthPx(layout) : boardBounds.width;
  if (!(uiW > 0)) uiW = boardBounds.width;
  return computeMockupUiScale(uiW).scaleUi;
}

// Day (t=0.25) and night (t=0.75) palettes lifted straight from day.html /
// night.html (--sky-top / --sky-mid / --sky-horizon). Dawn (0/1) and sunset (0.5)
// are artistic transition frames the mockups don't define.
export var SKY_KEYFRAMES = [
  // Dawn: mostly the day blue, just a little warmer, with a soft peach horizon
  // glow — not the strong red it used to be.
  { t: 0, top: [120, 182, 228], mid: [200, 212, 228], bottom: [252, 226, 205] },
  { t: 0.25, top: [91, 200, 245], mid: [158, 224, 251], bottom: [223, 246, 255] },
  { t: 0.5, top: [90, 110, 180], mid: [255, 150, 90], bottom: [255, 100, 60] },
  { t: 0.75, top: [11, 30, 77], mid: [22, 58, 107], bottom: [44, 92, 134] },
  { t: 1, top: [120, 182, 228], mid: [200, 212, 228], bottom: [252, 226, 205] },
];
